import React, { useState } from 'react';
import { Modal, Heading, Text } from './ui';

const SessionResumeModal = ({ 
    session, 
    onResume, 
    onRestart, 
    onCancel 
}) => {
    const [showRestartWarning, setShowRestartWarning] = useState(false);
    const [isRestarting, setIsRestarting] = useState(false);

    const colorTokens = {
        oxfordBlue: '#1b263b',
        neutral: {
            0: '#FFFFFF',
            50: '#F9F8F6',
            900: '#0D0D0D'
        }
    };

    // Calculate session statistics
    const getSessionStats = () => {
        const stats = {
            currentStep: session.current_step || 0,
            totalSteps: session.total_steps || 0,
            photosCount: 0,
            checkInsCount: 0,
            timeElapsed: 0
        };

        // Parse session report to get statistics
        if (session.session_report) {
            const report = typeof session.session_report === 'string' 
                ? JSON.parse(session.session_report) 
                : session.session_report;

            if (report.timeline && Array.isArray(report.timeline)) {
                report.timeline.forEach(entry => {
                    if (entry.type === 'photo') stats.photosCount++;
                    if (entry.type === 'check_in') stats.checkInsCount++;
                });
            }
        }

        // Calculate time elapsed
        if (session.started_at) {
            const start = new Date(session.started_at);
            const end = session.paused_at ? new Date(session.paused_at) : new Date();
            stats.timeElapsed = Math.floor((end - start) / 1000 / 60); // minutes
        }

        return stats;
    };

    const stats = getSessionStats();

    const handleRestart = async () => {
        setIsRestarting(true);
        try {
            await onRestart();
        } finally {
            setIsRestarting(false);
        }
    };

    if (showRestartWarning) {
        return (
            <Modal onClose={() => setShowRestartWarning(false)} maxWidth="max-w-2xl">
                <div className="minimal-padding">
                    <div className="flex items-center justify-between mb-6">
                        <Heading level={2} style={{ color: colorTokens.oxfordBlue }}>
                            Confirm Restart
                        </Heading>
                        <button
                            onClick={() => setShowRestartWarning(false)}
                            className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0 modal-close"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="bg-neutral-50 p-4 hairline mb-6">
                        <div className="flex items-start space-x-3">
                            <div className="text-2xl">⚠️</div>
                            <div className="flex-1">
                                <Text className="text-neutral-900 mb-2">
                                    Restarting this session will permanently delete:
                                </Text>
                                <ul className="list-disc list-inside space-y-1 text-sm text-neutral-900 opacity-80">
                                    <li>{stats.photosCount} photo{stats.photosCount !== 1 ? 's' : ''} captured</li>
                                    <li>{stats.checkInsCount} check-in{stats.checkInsCount !== 1 ? 's' : ''} recorded</li>
                                    <li>Your current progress (Step {stats.currentStep} of {stats.totalSteps})</li>
                                    <li>AI-generated tips based on this session</li>
                                    <li>All timeline data and notes</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <Text className="text-neutral-900 opacity-60 mb-6">
                        This action cannot be undone. Are you sure you want to start over?
                    </Text>

                    <div className="flex space-x-3">
                        <button
                            onClick={() => setShowRestartWarning(false)}
                            className="flex-1 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleRestart}
                            disabled={isRestarting}
                            className="flex-1 px-4 py-2 text-white hover:opacity-60 transition-opacity font-mono text-sm uppercase tracking-wider border-none"
                            style={{ 
                                backgroundColor: colorTokens.oxfordBlue,
                                opacity: isRestarting ? 0.6 : 1
                            }}
                        >
                            {isRestarting ? 'Restarting...' : 'Yes, Restart'}
                        </button>
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal onClose={onCancel} maxWidth="max-w-3xl">
            <div className="minimal-padding">
                <div className="flex items-center justify-between mb-6">
                    <Heading level={2} style={{ color: colorTokens.oxfordBlue }}>
                        Resume Cooking Session
                    </Heading>
                    <button
                        onClick={onCancel}
                        className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0 modal-close"
                    >
                        ✕
                    </button>
                </div>

                <div className="mb-6">
                    <Text size="lg" className="text-neutral-900 font-medium mb-4">
                        {session.recipe_title}
                    </Text>
                    <Text className="text-neutral-900 opacity-60">
                        You have an in-progress session for this recipe. Would you like to continue where you left off or start fresh?
                    </Text>
                </div>

                {/* Session Progress Summary */}
                <div className="bg-neutral-50 p-4 hairline mb-6">
                    <div className="font-mono uppercase text-sm tracking-wider text-neutral-900 mb-3">
                        Session Progress
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <div className="text-2xl font-mono text-neutral-900 mb-1">
                                {stats.currentStep}/{stats.totalSteps}
                            </div>
                            <div className="text-xs text-neutral-900 opacity-60 uppercase tracking-wider font-mono">
                                Steps
                            </div>
                        </div>
                        <div>
                            <div className="text-2xl font-mono text-neutral-900 mb-1">
                                {stats.photosCount}
                            </div>
                            <div className="text-xs text-neutral-900 opacity-60 uppercase tracking-wider font-mono">
                                Photos
                            </div>
                        </div>
                        <div>
                            <div className="text-2xl font-mono text-neutral-900 mb-1">
                                {stats.checkInsCount}
                            </div>
                            <div className="text-xs text-neutral-900 opacity-60 uppercase tracking-wider font-mono">
                                Check-ins
                            </div>
                        </div>
                        <div>
                            <div className="text-2xl font-mono text-neutral-900 mb-1">
                                {stats.timeElapsed}m
                            </div>
                            <div className="text-xs text-neutral-900 opacity-60 uppercase tracking-wider font-mono">
                                Elapsed
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                    <button
                        onClick={onResume}
                        className="w-full px-4 py-3 text-white hover:opacity-60 transition-opacity font-mono text-sm uppercase tracking-wider border-none text-left"
                        style={{ backgroundColor: colorTokens.oxfordBlue }}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="mb-1">Resume Session</div>
                                <div className="text-xs opacity-80 normal-case tracking-normal">
                                    Continue from Step {stats.currentStep} with all your data
                                </div>
                            </div>
                            <div className="text-2xl">→</div>
                        </div>
                    </button>

                    <button
                        onClick={() => setShowRestartWarning(true)}
                        className="w-full px-4 py-3 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider text-left"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="mb-1">Start Over</div>
                                <div className="text-xs opacity-60 normal-case tracking-normal">
                                    Begin a fresh session (will delete current progress)
                                </div>
                            </div>
                            <div className="text-2xl">↻</div>
                        </div>
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default SessionResumeModal;

